import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  LayoutDashboard,
  ScrollText,
  ShieldCheck,
  Settings,
  LogOut,
  Stethoscope,
  Target,
  Workflow as WorkflowIcon,
  Sun,
  Moon,
  Upload,
  UserCog,
  KeyRound,
  Gauge,
  Search,
  FileSearch,
  CreditCard,
  Building,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { ROLE_RANK, type UserRole } from '@praxara/shared/constants';
import { cn } from '@/lib/utils';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user, clear } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [search, setSearch] = useState('');

  // Fuzzy search tenant data only when opened + text entered
  const documents = useQuery({
    queryKey: ['cmdk', 'documents', search],
    queryFn: async () => (await api.get(`/api/documents?q=${encodeURIComponent(search)}&pageSize=10`)).data,
    enabled: open && search.length > 0,
    staleTime: 30_000,
  });

  const pvCases = useQuery({
    queryKey: ['cmdk', 'pv', search],
    queryFn: async () => (await api.get(`/api/pv/cases?q=${encodeURIComponent(search)}&pageSize=5`)).data,
    enabled: open && search.length > 0 && !!user && ROLE_RANK[user.role] >= ROLE_RANK['OPERATOR'],
    staleTime: 30_000,
  });

  const go = (to: string) => {
    onOpenChange(false);
    setSearch('');
    navigate(to);
  };

  const canSee = (min?: UserRole): boolean => {
    if (!min) return true;
    if (!user) return false;
    return ROLE_RANK[user.role] >= ROLE_RANK[min];
  };

  const navItems = useMemo(
    () => [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, keywords: 'home overview' },
      { to: '/documents', label: 'Documents', icon: FileText, keywords: 'files upload pdf' },
      { to: '/workflows', label: 'Workflows', icon: WorkflowIcon, keywords: 'runs jobs' },
      { to: '/pv', label: 'Pharmacovigilance cases', icon: Stethoscope, keywords: 'pv safety adverse ae' },
      { to: '/compliance', label: 'Compliance scanner', icon: Target, minRole: 'MANAGER' as UserRole, keywords: 'psmf sop gap' },
      { to: '/compliance-register', label: 'Compliance register', icon: ShieldCheck, minRole: 'MANAGER' as UserRole, keywords: 'findings risks' },
      { to: '/audit', label: 'Audit trail', icon: ScrollText, minRole: 'REVIEWER' as UserRole, keywords: 'log history integrity' },
      { to: '/admin', label: 'Admin', icon: Settings, minRole: 'ADMIN' as UserRole, keywords: 'tenant users' },
    ],
    [],
  );

  const settingsItems = useMemo(
    () => [
      { to: '/settings/profile', label: 'Profile', icon: UserCog, keywords: 'account password' },
      { to: '/settings/security', label: 'Security + 2FA + sessions', icon: KeyRound, keywords: 'mfa totp authenticator' },
      { to: '/settings/team', label: 'Team members', icon: UserCog, minRole: 'ADMIN' as UserRole, keywords: 'invite users roles' },
      { to: '/settings/tenant', label: 'Organisation', icon: Building, minRole: 'ADMIN' as UserRole, keywords: 'tenant branding region' },
      { to: '/settings/usage', label: 'Usage + credits', icon: Gauge, minRole: 'MANAGER' as UserRole, keywords: 'quota pages' },
      { to: '/settings/billing', label: 'Billing + invoices', icon: CreditCard, minRole: 'ADMIN' as UserRole, keywords: 'stripe subscription' },
    ],
    [],
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      className={cn(
        'fixed left-1/2 top-[18vh] z-50 w-full max-w-xl -translate-x-1/2 rounded-lg border bg-popover shadow-2xl',
        'data-[state=closed]:animate-none',
      )}
      overlayClassName="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm"
    >
      <div className="flex items-center border-b px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Jump to page, search documents, run an action..."
          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        <kbd className="pointer-events-none ml-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:flex">
          ESC
        </kbd>
      </div>

      <Command.List className="max-h-[60vh] overflow-y-auto p-1">
        <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
          No matches.
        </Command.Empty>

        <Command.Group heading="Navigate" className="cmdk-group">
          {navItems.filter((n) => canSee(n.minRole as UserRole | undefined)).map((item) => (
            <Command.Item
              key={item.to}
              value={`${item.label} ${item.keywords}`}
              onSelect={() => go(item.to)}
              className="cmdk-item"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </Command.Item>
          ))}
        </Command.Group>

        <Command.Group heading="Settings" className="cmdk-group">
          {settingsItems.filter((n) => canSee(n.minRole as UserRole | undefined)).map((item) => (
            <Command.Item
              key={item.to}
              value={`settings ${item.label} ${item.keywords}`}
              onSelect={() => go(item.to)}
              className="cmdk-item"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </Command.Item>
          ))}
        </Command.Group>

        <Command.Group heading="Actions" className="cmdk-group">
          <Command.Item
            value="upload document file"
            onSelect={() => go('/documents')}
            className="cmdk-item"
          >
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span>Upload a document</span>
          </Command.Item>
          <Command.Item
            value="toggle theme dark light mode"
            onSelect={() => {
              toggleTheme();
            }}
            className="cmdk-item"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Moon className="h-4 w-4 text-muted-foreground" />
            )}
            <span>Switch to {theme === 'dark' ? 'light' : 'dark'} theme</span>
          </Command.Item>
          <Command.Item
            value="sign out logout"
            onSelect={() => {
              clear();
              onOpenChange(false);
              navigate('/login');
            }}
            className="cmdk-item"
          >
            <LogOut className="h-4 w-4 text-muted-foreground" />
            <span>Sign out</span>
          </Command.Item>
        </Command.Group>

        {search.length > 0 && documents.data?.items?.length > 0 && (
          <Command.Group heading="Documents" className="cmdk-group">
            {documents.data.items.slice(0, 8).map((d: { id: string; fileName: string; documentType?: string | null }) => (
              <Command.Item
                key={d.id}
                value={`doc ${d.fileName} ${d.documentType ?? ''}`}
                onSelect={() => go(`/documents/${d.id}`)}
                className="cmdk-item"
              >
                <FileSearch className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 truncate">
                  <div className="truncate">{d.fileName}</div>
                  {d.documentType && (
                    <div className="truncate text-[10px] text-muted-foreground">{d.documentType}</div>
                  )}
                </div>
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {search.length > 0 && pvCases.data?.items?.length > 0 && (
          <Command.Group heading="Pharmacovigilance cases" className="cmdk-group">
            {pvCases.data.items.slice(0, 5).map((c: { id: string; caseIdentifier: string; productName?: string | null; status: string }) => (
              <Command.Item
                key={c.id}
                value={`pv case ${c.caseIdentifier} ${c.productName ?? ''}`}
                onSelect={() => go(`/pv/${c.id}`)}
                className="cmdk-item"
              >
                <Stethoscope className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 truncate">
                  <div className="truncate font-mono text-xs">{c.caseIdentifier}</div>
                  {c.productName && (
                    <div className="truncate text-[10px] text-muted-foreground">{c.productName}</div>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.status}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>

      <div className="flex items-center justify-between border-t px-3 py-2 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 font-mono">??</kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 font-mono">?</kbd> open
          </span>
        </div>
        <span>Praxara command palette</span>
      </div>
    </Command.Dialog>
  );
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return { open, setOpen };
}
